import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Users, Check } from "lucide-react";
import { useLanguage } from "@/lib/LanguageContext";
import api, { formatPrice } from "@/lib/api";

export default function HouseDetailPage() {
  const { id } = useParams();
  const [house, setHouse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);
  const { t, l, lang } = useLanguage();

  useEffect(() => {
    api.get(`/houses/${id}`).then((r) => setHouse(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="py-32 text-center text-muted-foreground">{t("common.loading")}</div>;
  if (!house) return <div className="py-32 text-center"><h2 className="font-serif text-3xl">{t("houses.notFound")}</h2></div>;

  const amenities = house[`amenities_${lang}`] || house.amenities || [];

  return (
    <div className="py-24 md:py-32" data-testid="house-detail-page">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <Link to="/houses" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 text-sm" data-testid="back-to-houses">
          <ArrowLeft className="w-4 h-4" /> {t("nav.houses")}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Photos */}
          <div>
            <div className="aspect-[4/3] overflow-hidden bg-card mb-3">
              <img
                src={house.photos?.[activePhoto] || ""}
                alt={house.name}
                className="w-full h-full object-cover"
                data-testid="house-main-photo"
              />
            </div>
            {house.photos?.length > 1 && (
              <div className="flex gap-2">
                {house.photos.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => setActivePhoto(i)}
                    className={`w-20 h-16 overflow-hidden border-2 transition-colors ${i === activePhoto ? "border-primary" : "border-transparent opacity-60 hover:opacity-100"}`}
                    data-testid={`photo-thumb-${i}`}
                  >
                    <img src={p} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <Badge className="bg-primary/10 text-primary border-0 mb-4">{t(`houses.${house.house_type}`)}</Badge>
            <h1 className="text-4xl md:text-5xl font-serif tracking-tight mb-4" data-testid="house-name">{l(house)}</h1>
            <p className="text-muted-foreground leading-relaxed mb-6">{l(house, "description")}</p>

            <div className="flex items-center gap-6 mb-8">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                <span className="text-sm">{house.capacity_min}-{house.capacity_max} {t("common.persons")}</span>
              </div>
              <div className="text-2xl font-serif text-primary" data-testid="house-price">
                {formatPrice(house.price_per_hour)} <span className="text-sm text-muted-foreground font-sans">{t("common.perHour")}</span>
              </div>
            </div>

            {/* Amenities */}
            {amenities.length > 0 && (
              <div className="mb-8">
                <h3 className="font-sans font-semibold text-sm uppercase tracking-widest mb-3">{t("houses.amenities")}</h3>
                <div className="flex flex-wrap gap-2">
                  {amenities.map((a, i) => (
                    <span key={i} className="flex items-center gap-1.5 text-sm text-muted-foreground bg-card px-3 py-1.5 border border-border">
                      <Check className="w-3 h-3 text-primary" /> {a}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Video */}
            {house.video_url && (
              <div className="mb-8">
                <h3 className="font-sans font-semibold text-sm uppercase tracking-widest mb-3">{t("houses.videoTour")}</h3>
                <div className="aspect-video bg-card">
                  <iframe src={house.video_url} className="w-full h-full" allowFullScreen title="Video tour" />
                </div>
              </div>
            )}

            {/* Book Button */}
            <Link to={`/booking?house=${house.id}`} data-testid="house-book-btn">
              <Button className="w-full bg-primary text-primary-foreground py-6 rounded-none uppercase tracking-widest text-sm font-medium hover:bg-primary/90 btn-shine">
                {t("houses.bookThis")}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
